import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ClassifierNav from '../components/ClassifierNav';
import Header from '../components/Header';
import HeadlineItem from '../components/HeadlineItem';
import HeadlineList from '../components/HeadlineList';
import PullToRefresh from '../components/PullToRefresh';
import SourceNav from '../components/SourceNav';
import { useHeadlineStore } from '../stores/headlineStore';
import { useSettingsStore } from '../stores/settingsStore';
import type { Headline } from '../types';

export default function HeadlinesPage() {
  const { slug, classifierId: classifierIdParam } = useParams<{
    slug: string;
    classifierId: string;
  }>();
  const isAggregate = !slug && !classifierIdParam;
  const classifierId = classifierIdParam
    ? parseInt(classifierIdParam, 10)
    : null;

  const sources = useSettingsStore((s) => s.sources);
  const hiddenIds = useSettingsStore((s) => s.hiddenIds);
  const classifiers = useSettingsStore((s) => s.classifiers);
  const fetchSources = useSettingsStore((s) => s.fetchSources);

  const unread = useHeadlineStore((s) => s.unread);
  const read = useHeadlineStore((s) => s.read);
  const summarizedIds = useHeadlineStore((s) => s.summarizedIds);
  const fetchHeadlines = useHeadlineStore((s) => s.fetchHeadlines);
  const markAllRead = useHeadlineStore((s) => s.markAllRead);

  const fetchOpts = useMemo(
    () => ({
      sourceSlug: slug,
      classifierId: classifierId ?? undefined,
    }),
    [slug, classifierId],
  );

  const summarizedSet = new Set(summarizedIds);
  const summarizedHeadlines = summarizedIds
    .map((id) => unread.find((h) => h.id === id))
    .filter((h): h is Headline => h !== undefined);
  const unpinnedUnread =
    summarizedHeadlines.length > 0
      ? unread.filter((h) => !summarizedSet.has(h.id))
      : unread;

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  useEffect(() => {
    fetchHeadlines(fetchOpts);
  }, [fetchOpts, fetchHeadlines]);

  const handleRefresh = useCallback(() => {
    return fetchHeadlines(fetchOpts);
  }, [fetchOpts, fetchHeadlines]);

  const showSource = isAggregate || classifierId !== null;

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="page-headlines">
        <Header />
        <SourceNav
          sources={sources}
          hiddenIds={hiddenIds}
          activeSlug={classifierId ? undefined : slug || null}
        />
        <ClassifierNav
          classifiers={classifiers}
          activeClassifierId={classifierId}
        />

        {unread.length > 0 && (
          <div className="mark-all">
            <button type="button" onClick={() => markAllRead(fetchOpts)}>
              Mark All Read
            </button>
          </div>
        )}

        <div style={{ display: 'flow-root', overflowAnchor: 'none' }}>
          {summarizedHeadlines.length > 0 && (
            <ul>
              {summarizedHeadlines.map((h) => (
                <HeadlineItem
                  key={h.id}
                  headline={h}
                  showSource={showSource}
                  isRead={false}
                  pinned
                />
              ))}
            </ul>
          )}
        </div>

        <HeadlineList
          headlines={unpinnedUnread}
          showSource={showSource}
          isRead={false}
          emptyMessage="No unread headlines."
        />

        {unread.length > 0 && (
          <div className="mark-all">
            <button type="button" onClick={() => markAllRead(fetchOpts)}>
              Mark All Read
            </button>
          </div>
        )}

        <h2>Previously Read</h2>
        <HeadlineList
          headlines={read}
          showSource={showSource}
          isRead={true}
          emptyMessage="No read headlines yet."
        />
      </div>
    </PullToRefresh>
  );
}
